import type { ColumnDef } from '@tanstack/react-table';
import { Users } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { HistorySection } from '@/features/audit-log';
import { usePermissions } from '@/features/auth';
import { CustomerCouponsSection } from '@/features/coupons';
import { CustomerPointsSection } from '@/features/loyalty';
import { useMarketTimeZone, useRecordTimeZone, useSelectedCountry } from '@/features/country-scope';
import { ApiError } from '@/shared/api/api-error';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { DataTableToolbar } from '@/shared/components/data-table/data-table-toolbar';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { ExportActions } from '@/shared/components/export-actions';
import { NotFoundPage } from '@/shared/components/errors/not-found-page';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { SearchInput } from '@/shared/components/search-input';
import { StatusBadge } from '@/shared/components/status-badge';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';
import { formatDateTime } from '@/shared/lib/format';

import { exportCustomers } from '../api/customers.api';
import { useCustomer, useCustomers } from '../api/customers.queries';
import { CustomerAccountActions } from '../components/customer-account-actions';
import { CustomerComplaintsCard, CustomerRefundsCard } from '../components/customer-activity';
import { CustomerLimitsSection } from '../components/customer-limits-section';
import type { Customer } from '../types/customer.types';

/** The columns, with times in the page's market zone. */
function columns(zone: string): ColumnDef<Customer, unknown>[] {
  return [
    {
      id: 'name',
      header: 'Customer',
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="min-w-0">
          <Link
            to={`/customers/${row.original.id}`}
            className="text-primary font-medium hover:underline"
          >
            {row.original.name ?? `#${row.original.id}`}
          </Link>
          <span className="text-muted-foreground block text-[11px]">
            {row.original.email ?? '—'}
          </span>
        </div>
      ),
    },
    {
      id: 'msisdn',
      header: 'Phone',
      enableSorting: false,
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.msisdn ?? '—'}</span>,
    },
    {
      id: 'country',
      header: 'Country',
      enableSorting: false,
      cell: ({ row }) => row.original.countryName ?? '—',
    },
    {
      id: 'orders',
      header: () => <div className="text-end">Orders</div>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-end tabular-nums">
          {row.original.orderCount}
          {row.original.failedOrderCount > 0 && (
            <span className="text-status-danger"> ({row.original.failedOrderCount} failed)</span>
          )}
        </div>
      ),
    },
    {
      id: 'last',
      header: 'Last order',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatDateTime(row.original.lastOrderAt, zone)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Account',
      enableSorting: false,
      cell: ({ row }) => (
        <StatusBadge variant={row.original.isActive ? 'success' : 'neutral'}>
          {row.original.isActive ? 'Active' : 'Inactive'}
        </StatusBadge>
      ),
    },
  ];
}

/** Storefront customers in the selected country, with their order activity. Read-only. */
export function CustomersPage() {
  const tableState = useTableSearchParams();
  const { country } = useSelectedCountry();
  const zone = useMarketTimeZone();
  const tableColumns = useMemo(() => columns(zone), [zone]);
  const search = useDebouncedValue(tableState.search, 300);

  const params = useMemo(
    () => ({
      page: tableState.page,
      pageSize: tableState.pageSize,
      search: search || undefined,
      countryId: country?.id,
    }),
    [tableState.page, tableState.pageSize, search, country?.id],
  );
  const customers = useCustomers(params);
  const table = useDataTable({
    data: customers.data,
    columns: tableColumns,
    tableState,
    getRowId: (c) => String(c.id),
  });

  return (
    <PageContainer>
      <PageHeader
        title="Customers"
        description="Storefront accounts and their orders. Search by name, phone or email."
        actions={
          <ExportActions
            excel={() => exportCustomers({ search: search || undefined, countryId: country?.id })}
          />
        }
      />
      <div className="flex flex-col gap-3">
        <DataTableToolbar
          hasActiveFilters={tableState.hasActiveFilters}
          onResetFilters={tableState.resetFilters}
        >
          <SearchInput
            value={tableState.search}
            onChange={tableState.setSearch}
            placeholder="Search customers…"
            className="w-full sm:w-64"
          />
        </DataTableToolbar>
        <DataTable
          table={table}
          isLoading={customers.isLoading}
          isFetching={customers.isFetching}
          error={customers.error}
          onRetry={() => void customers.refetch()}
          emptyState={
            tableState.hasActiveFilters ? (
              <EmptyState
                icon={Users}
                title="No customers match"
                description="Try another name, phone or email."
                action={
                  <Button variant="outline" size="sm" onClick={tableState.resetFilters}>
                    Clear search
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Users}
                title="No customers"
                description="No storefront accounts in this country yet."
              />
            )
          }
        />
        <DataTablePagination table={table} />
      </div>
    </PageContainer>
  );
}

/**
 * One customer: profile, account actions (customers.manage), and their
 * complaints and refunds. Their orders are on the Orders page, filtered to them.
 */
export function CustomerPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const parsedId = Number(customerId);
  const isValidId = Number.isInteger(parsedId) && parsedId > 0;
  const customer = useCustomer(isValidId ? parsedId : undefined);
  const { can } = usePermissions();
  // The customer's own market, whatever the header shows.
  const { zone, note: zoneNote } = useRecordTimeZone(customer.data?.countryId);

  if (!isValidId) return <NotFoundPage />;
  if (customer.isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-40" />
      </PageContainer>
    );
  }
  if (
    customer.error instanceof ApiError &&
    (customer.error.isNotFound || customer.error.isForbidden)
  ) {
    return <NotFoundPage />;
  }
  if (customer.error || !customer.data) {
    return (
      <PageContainer>
        <ErrorState error={customer.error} onRetry={() => void customer.refetch()} />
      </PageContainer>
    );
  }

  const data = customer.data;
  return (
    <PageContainer>
      <PageHeader
        backTo={{ to: '/customers', label: 'Customers' }}
        title={data.name ?? `Customer #${data.id}`}
        description={[data.countryName, zoneNote].filter(Boolean).join(' · ') || undefined}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to={`/orders?customerId=${data.id}`}>See their orders</Link>
            </Button>
            {can('customers.manage') && <CustomerAccountActions customer={data} />}
          </div>
        }
      />
      <Card>
        <CardContent className="pt-4">
          <dl className="grid gap-4 text-sm sm:grid-cols-3">
            <Field label="Phone" value={data.msisdn ?? '—'} />
            <Field label="Email" value={data.email ?? '—'} />
            <Field label="User name" value={data.userName ?? '—'} />
            <Field label="Orders" value={`${data.orderCount} (${data.failedOrderCount} failed)`} />
            <Field label="Last order" value={formatDateTime(data.lastOrderAt, zone)} />
            <Field label="Account" value={data.isActive ? 'Active' : 'Inactive'} />
          </dl>
        </CardContent>
      </Card>
      {(can('complaints.manage') || can('refunds.request')) && (
        <div className="grid gap-4 md:grid-cols-2">
          {can('complaints.manage') && <CustomerComplaintsCard customerId={data.id} zone={zone} />}
          {can('refunds.request') && <CustomerRefundsCard customerId={data.id} zone={zone} />}
        </div>
      )}
      <CustomerLimitsSection
        customerId={data.id}
        customerName={data.name ?? data.userName ?? `#${data.id}`}
      />
      {can('loyalty.read') && (
        <CustomerPointsSection
          customerId={data.id}
          customerName={data.name ?? data.userName ?? `#${data.id}`}
          timeZone={zone}
        />
      )}
      {can('coupons.read') && (
        <CustomerCouponsSection
          customerId={data.id}
          customerName={data.name ?? data.userName ?? `#${data.id}`}
          timeZone={zone}
        />
      )}
      <HistorySection entityType="Customer" entityId={String(data.id)} timeZone={zone} />
    </PageContainer>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {label}
      </dt>
      <dd className="mt-1 truncate">{value}</dd>
    </div>
  );
}
